/**
 * 차단 연락처 관리 — 통화 녹음이 클라우드로 절대 가지 않게 지정한 사람들.
 *
 * 동작 원칙:
 *  - 데이터는 디바이스 내부 EncryptedSharedPreferences에만. 서버 sync 없음.
 *  - 매칭은 번호 기반 (E.164 정규화 후 마지막 8자리 fallback).
 *  - 동명이인/연락처 미등록 번호는 차단 불가 — 사용자에게 명시.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { BlockedContact, BlockedSkipStats, ContactPickedDetail } from '@butler/shared';
import { bridge } from '../lib/bridge';
import { PageHeader } from '../components/AppShell';

function uuid(): string {
  // crypto.randomUUID는 안드 WebView도 지원 (Chromium 92+)
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `bc_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function loadList(): BlockedContact[] {
  const json = bridge().getBlockedContacts?.() ?? '[]';
  try {
    const arr = JSON.parse(json) as unknown;
    if (!Array.isArray(arr)) return [];
    return arr as BlockedContact[];
  } catch {
    return [];
  }
}

function saveList(next: BlockedContact[]) {
  bridge().setBlockedContacts?.(JSON.stringify(next));
}

function loadStats(): BlockedSkipStats {
  const json = bridge().getBlockedSkipStats?.() ?? '{"count":0,"lastAtMs":0}';
  try {
    return JSON.parse(json) as BlockedSkipStats;
  } catch {
    return { count: 0, lastAtMs: 0 };
  }
}

export function BlockedContacts() {
  const navigate = useNavigate();
  const [list, setList] = useState<BlockedContact[]>(() => loadList());
  const [stats, setStats] = useState<BlockedSkipStats>(() => loadStats());
  const [picking, setPicking] = useState(false);

  useEffect(() => {
    function onPicked(e: Event) {
      setPicking(false);
      const detail = (e as CustomEvent<ContactPickedDetail & { error?: string }>).detail;
      if (!detail) return;
      if (detail.cancelled) {
        if ('error' in detail && detail.error === 'permission_denied') {
          alert('주소록 권한이 필요합니다. 설정에서 권한을 허용한 뒤 다시 시도해주세요.');
        }
        return;
      }
      if (!detail.numbersE164 || detail.numbersE164.length === 0) {
        alert(`"${detail.displayName}"에 등록된 번호가 없어 차단할 수 없습니다.`);
        return;
      }
      const exists = list.some((c) =>
        c.numbersE164.some((n) => detail.numbersE164.includes(n)),
      );
      if (exists) {
        alert('이미 차단된 번호가 포함된 연락처입니다.');
        return;
      }
      const next: BlockedContact[] = [
        ...list,
        {
          id: uuid(),
          displayName: detail.displayName,
          numbersE164: detail.numbersE164,
        },
      ];
      setList(next);
      saveList(next);
    }
    function onPerm(e: Event) {
      const detail = (e as CustomEvent<{ name: string; granted: boolean }>).detail;
      // contacts 권한이 방금 grant 됐고 우리가 picker 대기 중이면 자동으로 picker 재호출
      if (detail?.name === 'contacts' && detail.granted && picking) {
        bridge().pickContact?.();
      }
    }
    window.addEventListener('butler:contact-picked', onPicked);
    window.addEventListener('butler:permission-changed', onPerm);
    return () => {
      window.removeEventListener('butler:contact-picked', onPicked);
      window.removeEventListener('butler:permission-changed', onPerm);
    };
  }, [list, picking]);

  // stats는 진입할 때 + 30초마다 polling (Worker가 백그라운드에서 카운트 올림)
  useEffect(() => {
    const t = window.setInterval(() => setStats(loadStats()), 30_000);
    return () => window.clearInterval(t);
  }, []);

  function onAdd() {
    if (!bridge().pickContact) {
      alert('연락처 선택은 안드로이드 앱에서만 동작합니다.');
      return;
    }
    setPicking(true);
    bridge().pickContact?.();
    // 사용자가 picker 자체에서 백버튼 누를 수도 있으니 5초 후 자동 reset
    window.setTimeout(() => setPicking(false), 5_000);
  }

  function onRemove(c: BlockedContact) {
    if (!window.confirm(`"${c.displayName}" 차단을 해제할까요?\n앞으로 이 사람과의 통화 녹음이 클라우드에 업로드됩니다.`)) {
      return;
    }
    const next = list.filter((x) => x.id !== c.id);
    setList(next);
    saveList(next);
  }

  const lastAtLabel = stats.lastAtMs > 0
    ? new Date(stats.lastAtMs).toLocaleString('ko-KR', { dateStyle: 'short', timeStyle: 'short' })
    : null;

  return (
    <>
      <PageHeader
        title="차단된 연락처"
        subtitle="이 사람들과의 통화 녹음은 클라우드에 절대 올라가지 않아요."
        trailing={
          <button
            type="button"
            onClick={() => navigate('/more')}
            className="text-[12px] text-[var(--text-secondary)] px-sm py-[6px] rounded-md border border-[var(--border)] hover:text-[var(--text-primary)]"
            aria-label="더보기로"
          >
            ← 더보기
          </button>
        }
      />
      <main className="px-xl space-y-lg">
        {/* 안내 박스 */}
        <section className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-lg shadow-butler-md">
          <div className="text-[11px] uppercase tracking-wider text-[var(--text-muted)] mb-sm font-semibold">
            개인정보 보호
          </div>
          <p className="text-[12px] text-[var(--text-secondary)] leading-relaxed">
            여기에 추가한 사람과의 통화 녹음 파일은{' '}
            <strong className="text-[var(--text-primary)]">서버에 전송되지 않고 디바이스에만 남습니다.</strong>
            <br />
            매칭은 전화번호 기준이라, 연락처에 저장되지 않은 번호로 걸려온 경우는 차단되지 않을 수 있어요.
          </p>
        </section>

        {/* 통계 */}
        <section className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-lg shadow-butler-md">
          <div className="text-[11px] uppercase tracking-wider text-[var(--text-muted)] mb-sm font-semibold">
            차단 누적
          </div>
          <div className="flex items-baseline gap-md">
            <span className="text-[28px] font-mono text-[var(--text-primary)]">{stats.count}</span>
            <span className="text-[12px] text-[var(--text-muted)]">건 업로드 차단됨</span>
          </div>
          {lastAtLabel && (
            <div className="text-[11px] text-[var(--text-muted)] mt-xs">최근: {lastAtLabel}</div>
          )}
        </section>

        {/* 차단 리스트 */}
        <section className="bg-[var(--surface)] border border-[var(--border)] rounded-xl shadow-butler-md overflow-hidden">
          <div className="text-[11px] uppercase tracking-wider text-[var(--text-muted)] px-lg pt-lg pb-sm font-semibold flex items-center justify-between">
            <span>차단 목록 ({list.length})</span>
            <button
              type="button"
              onClick={onAdd}
              disabled={picking}
              className="text-[12px] px-md py-[6px] rounded-md bg-[var(--accent)] text-[var(--accent-on)] disabled:opacity-40"
            >
              {picking ? '연락처 선택 중…' : '+ 연락처 추가'}
            </button>
          </div>
          {list.length === 0 ? (
            <div className="px-lg py-xl text-center text-[12px] text-[var(--text-muted)]">
              아직 차단된 연락처가 없어요.
              <br />
              위 버튼으로 연락처를 골라 추가해보세요.
            </div>
          ) : (
            <ul className="divide-y divide-[var(--border)] border-t border-[var(--border)]">
              {list.map((c) => (
                <li key={c.id} className="px-lg py-md flex items-center gap-md">
                  <div className="flex-1 min-w-0">
                    <div className="text-[14px] text-[var(--text-primary)] truncate">
                      {c.displayName || '(이름 없음)'}
                    </div>
                    <div className="text-[12px] text-[var(--text-muted)] font-mono mt-[2px] truncate">
                      {c.numbersE164.join(', ')}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => onRemove(c)}
                    className="text-[12px] text-[var(--text-secondary)] px-sm py-[6px] rounded-md border border-[var(--border)] hover:text-red-600 hover:border-red-600/30"
                  >
                    해제
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* 한계 안내 */}
        <section className="text-[11px] text-[var(--text-muted)] leading-relaxed px-md">
          <strong className="text-[var(--text-secondary)]">알아두세요:</strong>
          <ul className="list-disc list-inside mt-xs space-y-[2px]">
            <li>연락처에 저장되지 않은 번호로 걸려온 통화는 차단되지 않을 수 있어요.</li>
            <li>이미 업로드된 녹음은 자동 삭제되지 않습니다. 통화 상세 화면에서 개별 삭제 가능.</li>
            <li>차단 목록은 이 휴대폰에만 저장되며 서버로 전송되지 않습니다.</li>
          </ul>
        </section>
      </main>
    </>
  );
}
